import { useRef, type CSSProperties, type KeyboardEvent, type ReactNode } from "react";

/**
 * GT-FLEET-CUSTOMER-READINESS-SHARED-UX-VEHICLES-DRIVERS-01 — shared
 * tab strip.
 *
 * A repo search before building found **no** Tabs primitive and at
 * least seven hand-rolled strips (`VehicleDetailPage`, `VehiclesPage`,
 * `DevicesPage`, `TripsTabs`, `TripsDataTab`, `MapDataModeSwitch`,
 * `SchedulesPage`, `ExportProfilesSection`). All of them set
 * `role="tab"` / `aria-selected` correctly but **none** implements the
 * WAI-ARIA keyboard pattern: every tab was a tab stop and the arrow
 * keys did nothing. This primitive fixes that once.
 *
 * Keyboard contract (WAI-ARIA Tabs, manual activation avoided —
 * selection follows focus, matching the existing click behaviour):
 *   - exactly one tab stop (roving `tabindex`),
 *   - ArrowLeft / ArrowRight move between tabs and wrap,
 *   - in RTL the arrow keys are swapped so "left" still means "the tab
 *     to the visual left",
 *   - Home / End jump to the first / last enabled tab,
 *   - disabled tabs are skipped.
 *
 * Presentation is intentionally minimal: the strip owns the tab chrome
 * and the caller owns the panel. `overflowX: auto` keeps a long strip
 * inside its own scroll container so a narrow viewport never gains
 * page-level horizontal overflow.
 *
 * Two looks, one behaviour. `variant="panel"` (default) draws the
 * folder-tab chrome the fleet pages have always used. `variant =
 * "underline"` draws plain text tabs with an accent underline on the
 * active one — the form the GTCC reference mockups use for their view
 * and sub-view strips. Only the chrome differs; keyboard contract,
 * roving tabindex and ids are identical, so the variant is a style
 * choice at the call site and never a second component.
 */
export interface TabItem {
  readonly id: string;
  readonly label: ReactNode;
  readonly disabled?: boolean;
  readonly testId?: string;
}

export interface TabsProps {
  readonly items: ReadonlyArray<TabItem>;
  readonly value: string;
  readonly onChange: (next: string) => void;
  /** Accessible name for the tablist. */
  readonly ariaLabel: string;
  /**
   * Prefix for the generated `id` attributes. Callers that render a
   * panel must use `${idPrefix}-panel-${id}` and point it back with
   * `aria-labelledby={`${idPrefix}-tab-${id}`}`.
   */
  readonly idPrefix: string;
  /** `"ltr"` (default) or `"rtl"` — drives arrow-key direction. */
  readonly direction?: "ltr" | "rtl";
  /** Chrome only: `"panel"` (default) or `"underline"`. */
  readonly variant?: TabsVariant;
  readonly dataTestId?: string;
}

export type TabsVariant = "panel" | "underline";

export function Tabs({
  items,
  value,
  onChange,
  ariaLabel,
  idPrefix,
  direction = "ltr",
  variant = "panel",
  dataTestId,
}: TabsProps) {
  const listRef = useRef<HTMLDivElement | null>(null);

  const enabled = items.filter((item) => !item.disabled);
  const activeIndexInEnabled = enabled.findIndex((item) => item.id === value);

  const focusTab = (id: string): void => {
    // Looked up by DOM id property rather than a `#id` selector so ids
    // containing selector-special characters need no CSS.escape.
    const nodes = listRef.current?.querySelectorAll<HTMLElement>('[role="tab"]') ?? [];
    Array.from(nodes)
      .find((node) => node.id === `${idPrefix}-tab-${id}`)
      ?.focus();
  };

  const move = (delta: number): void => {
    if (enabled.length === 0) return;
    const from = activeIndexInEnabled >= 0 ? activeIndexInEnabled : 0;
    const next = enabled[(from + delta + enabled.length) % enabled.length]!;
    onChange(next.id);
    focusTab(next.id);
  };

  const jump = (position: "first" | "last"): void => {
    if (enabled.length === 0) return;
    const next = position === "first" ? enabled[0]! : enabled[enabled.length - 1]!;
    onChange(next.id);
    focusTab(next.id);
  };

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>): void => {
    // In RTL the visual order is mirrored, so ArrowLeft must advance.
    const forward = direction === "rtl" ? "ArrowLeft" : "ArrowRight";
    const backward = direction === "rtl" ? "ArrowRight" : "ArrowLeft";
    switch (event.key) {
      case forward:
        event.preventDefault();
        move(1);
        break;
      case backward:
        event.preventDefault();
        move(-1);
        break;
      case "Home":
        event.preventDefault();
        jump("first");
        break;
      case "End":
        event.preventDefault();
        jump("last");
        break;
      default:
        break;
    }
  };

  return (
    <div
      ref={listRef}
      role="tablist"
      aria-label={ariaLabel}
      aria-orientation="horizontal"
      onKeyDown={onKeyDown}
      data-variant={variant}
      data-testid={dataTestId}
      style={{
        display: "flex",
        flexDirection: "row",
        gap: variant === "underline" ? "var(--gt-sys-space-4)" : "var(--gt-sys-space-1)",
        overflowX: "auto",
        maxInlineSize: "100%",
        scrollbarWidth: "thin",
      }}
    >
      {items.map((item) => {
        const isActive = item.id === value;
        const chrome: CSSProperties =
          variant === "underline"
            ? {
                /*
                  Text tabs: no box, no fill. The only mark of the active
                  tab is the accent rule under it, so the inactive tabs
                  carry a transparent rule of the same width and the row
                  never shifts on selection.
                */
                paddingBlock: "var(--gt-sys-space-2)",
                paddingInline: 0,
                border: "none",
                borderBlockEndWidth: 2,
                borderBlockEndStyle: "solid",
                borderBlockEndColor: isActive ? "var(--gt-sys-accent)" : "transparent",
                borderRadius: 0,
                background: "transparent",
                color: isActive ? "var(--gt-sys-accent)" : "var(--gt-sys-text-muted)",
                /* Text tabs are as wide as their label. */
                minInlineSize: 0,
              }
            : {
                paddingBlock: "var(--gt-sys-space-2)",
                paddingInline: "var(--gt-sys-space-3)",
                borderStartStartRadius: "var(--gt-sys-radius-md)",
                borderStartEndRadius: "var(--gt-sys-radius-md)",
                borderEndStartRadius: 0,
                borderEndEndRadius: 0,
                borderWidth: 1,
                borderStyle: "solid",
                borderColor: "var(--gt-sys-border)",
                borderBlockEndColor: isActive ? "transparent" : "var(--gt-sys-border)",
                background: isActive ? "var(--gt-sys-surface-raised)" : "transparent",
                color: isActive ? "var(--gt-sys-text)" : "var(--gt-sys-text-muted)",
                minInlineSize: "var(--gt-cmp-tabs-tab-min-inline-size)",
              };
        const tabStyle: CSSProperties = {
          flex: "0 0 auto",
          ...chrome,
          fontWeight: isActive
            ? "var(--gt-sys-font-weight-semibold)"
            : "var(--gt-sys-font-weight-medium)",
          fontSize: "var(--gt-sys-font-size-sm)",
          cursor: item.disabled ? "not-allowed" : "pointer",
          opacity: item.disabled ? 0.5 : 1,
          /*
            Component tokens, not a raw 40. Desktop keeps the compact tab
            height; under `@media (pointer: coarse)` both tokens resolve to
            `--gt-sys-touch-target-min` (44px) so a tab is a legal touch
            target on mobile web without a second mobile tab strip. The
            underline variant keeps the height for the same reason — only
            the inline padding goes away with the box.
          */
          minBlockSize: "var(--gt-cmp-tabs-tab-min-block-size)",
          whiteSpace: "nowrap",
        };
        return (
          <button
            key={item.id}
            type="button"
            role="tab"
            id={`${idPrefix}-tab-${item.id}`}
            aria-selected={isActive}
            aria-controls={`${idPrefix}-panel-${item.id}`}
            aria-disabled={item.disabled || undefined}
            disabled={item.disabled}
            /* Roving tabindex — the strip is a single tab stop. */
            tabIndex={isActive ? 0 : -1}
            onClick={() => {
              if (!item.disabled) onChange(item.id);
            }}
            data-testid={item.testId}
            style={tabStyle}
          >
            {item.label}
          </button>
        );
      })}
    </div>
  );
}
